import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import {
  getAccountId,
  getWhiteLabelPublisherId,
  isAdvertiser,
  isPublisher
} from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import useQueryParams from '~/hooks/useQuery'
import { PlatformNewsAlert } from '~/modules/retailMedia/components/PlatformNewsAlert'
import { useImpressionShareEnabled } from '~/modules/retailMedia/hooks/useImpressionShareEnabled'

const STORAGE_KEY = 'rm:alerts:impression-share-news:dismissed'

export function ImpressionShareNewsAlert(): JSX.Element | null {
  const { t } = useTranslation()
  const searchParams = useQueryParams()
  const { isWhiteLabel } = useAppInfo()
  const rmid = searchParams.get('rmid')

  // Same single-publisher scope as DashboardRetailMediaCampaigns / tables.
  // Multi-publisher aggregates without rmid stay unresolved → toast hidden.
  const publisherId = useMemo(() => {
    if (isPublisher()) return getAccountId() ?? null
    if (isWhiteLabel && isAdvertiser()) {
      return getWhiteLabelPublisherId() ?? null
    }
    return rmid
  }, [isWhiteLabel, rmid])

  const impressionShareEnabled = useImpressionShareEnabled({
    publisherId,
    advertiserId: isAdvertiser() ? getAccountId() : null,
    // News toast must not flash while permissions load, or show without scope.
    defaultEnabled: false
  })

  if (!impressionShareEnabled) return null

  return (
    <PlatformNewsAlert
      storageKey={STORAGE_KEY}
      learnMoreHref={t('rm:alerts.impressionShareNews.link')}
    >
      <Trans
        i18nKey="rm:alerts.impressionShareNews.message"
        components={{ title: <strong />, strong: <strong /> }}
      />
    </PlatformNewsAlert>
  )
}
